/**
 * System → Benachrichtigungsversand (DEV-126): form values of the dispatch
 * settings (pure, React-free). The server validates again and owns the rules.
 */
import { t } from '@lingui/core/macro';

import type { DispatchSetting, DispatchValue } from '../../lib/types';

/** What an input holds: text for numbers and words, a flag for a checkbox. */
export type RawValue = string | boolean;

/** The input value of a setting; a secret starts empty (empty keeps the stored one). */
export function rawOf(setting: DispatchSetting): RawValue {
  if (setting.type === 'boolean') return Boolean(setting.value ?? setting.default);
  if (setting.type === 'secret') return '';
  const value = setting.value ?? setting.default;
  return value === null || value === undefined ? '' : String(value);
}

/** The value to save, or an error in the UI language. */
export function parseSetting(setting: DispatchSetting, raw: RawValue): { value: DispatchValue; error: string | null } {
  if (setting.type === 'boolean') return { value: Boolean(raw), error: null };
  const text = String(raw).trim();
  if (setting.required && text === '' && !(setting.type === 'secret' && setting.configured)) {
    return { value: text, error: t`Pflichtangabe.` };
  }
  if (setting.type === 'text' || setting.type === 'secret' || setting.type === 'choice') return { value: text, error: null };
  if (text === '') return { value: null, error: t`Bitte einen Wert eingeben.` };
  const number = Number(text.replace(',', '.'));
  if (!Number.isFinite(number)) return { value: null, error: t`Zahl erwartet.` };
  if (setting.type === 'integer' && !Number.isInteger(number)) return { value: null, error: t`Ganze Zahl erwartet.` };
  const { minimum, maximum } = setting;
  if (minimum !== null && number < minimum) {
    return { value: null, error: t`Mindestens ${minimum}.` };
  }
  if (maximum !== null && number > maximum) {
    return { value: null, error: t`Höchstens ${maximum}.` };
  }
  return { value: number, error: null };
}

/** The changed values of *settings*, or null while one of them is invalid. */
export function changedValues(
  settings: readonly DispatchSetting[], raws: Readonly<Record<string, RawValue>>,
): Record<string, DispatchValue> | null {
  const changed: Record<string, DispatchValue> = {};
  for (const setting of settings) {
    const raw = raws[setting.key] ?? rawOf(setting);
    const { value, error } = parseSetting(setting, raw);
    if (error !== null) return null;
    if (setting.type === 'secret' ? value !== '' : value !== (setting.value ?? setting.default)) {
      changed[setting.key] = value;
    }
  }
  return changed;
}
